import React, {
    ForwardRefExoticComponent,
    RefAttributes,
    useRef,
    useState
} from "react";
import useRefresh from "./useRefresh";
import ReactDOM from "react-dom";

interface Options {
    remove?: boolean; // 关闭后移除
}

export default function useModalHandler<T, P extends object>(
    ModalComponent?:
        | ForwardRefExoticComponent<RefAttributes<T> & P>
        | JSX.Element,
    options?: Options
) {
    const [visible, setVisible] = useState(false);
    const [closed, setClosed] = useState(true);
    const [PAY_LOAD, SET_PAY_LOAD] = useState<unknown>();
    const [key, refresh] = useRefresh();
    const ref = useRef<T>();

    function close() {
        setVisible(false);
    }

    function open<T>(payload?: T) {
        ReactDOM.unstable_batchedUpdates(() => {
            setVisible(true);
            setClosed(false);
            SET_PAY_LOAD(payload || {});
        });
    }

    function afterClose() {
        setClosed(true);
        refresh();
    }

    /**
     * render输出的组件会在关闭后自动清理
     * 注意render方法会接管组件的visible, afterClose, onCancel, ref 四个属性.
     * @param children
     */
    function render(children: React.ReactElement) {
        const handleCancel = () => {
            close();
            children.props?.onCancel?.();
        };

        const handleAfterClose = () => {
            afterClose();
            children.props?.afterClose?.();
        };

        if (options?.remove && closed) return null;

        return React.cloneElement(children, {
            visible,
            PAY_LOAD,
            afterClose: handleAfterClose,
            onCancel: handleCancel,
            key: key,
            ref: ModalComponent ? ref : undefined
        });
    }

    const props = {
        visible,
        afterClose,
        onCancel: close
    };

    const modalRef = {
        visible,
        setVisible,
        PAY_LOAD,
        close,
        open,
        closed,
        afterClose,
        render,
        props
    };

    const proxyHandler: ProxyHandler<typeof modalRef & T> = {
        get: (target, prop, receiver) => {
            if (prop in modalRef) {
                return modalRef[prop as keyof typeof modalRef];
            } else {
                return ref.current?.[prop as keyof T];
            }
        }
    };

    return new Proxy<typeof modalRef & T>(modalRef as any, proxyHandler);
}
